import { type NodeViewProps } from '@tiptap/core'
import { NodeViewWrapper } from '@tiptap/react'
import _ from 'lodash'
import { Asterisk, Link2 } from 'lucide-react'
import { useState } from 'react'
import { getCustomizerWidth } from '#app/components/tiptap/hooks/useCustomizer.js'
import { Checkbox } from '#app/components/ui/checkbox.js'
import { Label } from '#app/components/ui/label.js'
import {
	Tooltip,
	TooltipTrigger,
	TooltipProvider,
	TooltipContent,
} from '#app/components/ui/tooltip.js'
import { cn } from '#app/utils/misc.js'
import { handleInputName } from '../utils'

export const Component = (props: NodeViewProps) => {
	const editable = props.editor.isEditable
	const required = props.node.attrs.required
	const maxWidth = getCustomizerWidth(props)
	const [value, setValue] = useState<string[]>([])

	return (
		<NodeViewWrapper
			className="flex flex-col gap-2"
			style={{
				width: String(maxWidth) + 'px',
				maxWidth: '100%',
			}}
		>
			{editable ? (
				<div className="flex flex-col gap-2">
					<div className="relative">
						<input
							type="text"
							maxLength={55}
							required={required}
							className="w-full appearance-none border-none bg-transparent font-medium outline-none"
							placeholder="Type something..."
							onChange={e => handleInputName(e.currentTarget.value, props)}
							value={props.node.attrs.placeholder}
						/>
						{required && (
							<div className="absolute right-3 top-1/2 -translate-y-1/2">
								<Asterisk width={16} className="input-tt-text" />
							</div>
						)}
						<div
							className={cn(
								'absolute',
								required
									? 'right-9 top-1/2 flex -translate-y-1/2 gap-2'
									: 'right-3 top-1/2 flex -translate-y-1/2 gap-2',
							)}
						>
							{props.node.attrs.to !== 'none' && (
								<>
									<TooltipProvider>
										<Tooltip>
											<TooltipTrigger>
												<Link2 width={16} />
											</TooltipTrigger>
											<TooltipContent>
												<p>
													This field is connected to the {props.node.attrs.to}{' '}
													field.
												</p>
											</TooltipContent>
										</Tooltip>
									</TooltipProvider>
								</>
							)}
						</div>
					</div>
					<div className="flex w-full flex-col gap-2">
						{props.node.attrs.options.split(',').map((option: string) => (
							<Label key={option} className="flex items-center gap-2">
								<Checkbox defaultChecked={false} className="checkbox-tt" />
								<p className="text-sm font-normal">{option}</p>
							</Label>
						))}
					</div>
				</div>
			) : (
				<>
					<input
						type="hidden"
						value={value.join(',')}
						required={required}
						name={
							props.node.attrs.to !== 'none'
								? props.node.attrs.to
								: `data_${_.camelCase(props.node.attrs.name)}`
						}
					/>
					<div className="flex w-full flex-col gap-2">
						<p className="text-sm font-medium">
							{props.node.attrs.placeholder}
						</p>
						<div className="flex w-full flex-col gap-2">
							{props.node.attrs.options.split(',').map((option: string) => (
								<Label key={option} className="flex items-center gap-2">
									<Checkbox
										key={option}
										value={option}
										className="checkbox-tt"
										onCheckedChange={checked => {
											setValue(prev => {
												if (checked) {
													return [...prev, option]
												}
												return prev.filter(v => v !== option)
											})
										}}
									>
										{option}
									</Checkbox>
									<p className="text-sm font-normal">{option}</p>
								</Label>
							))}
						</div>
					</div>
				</>
			)}
		</NodeViewWrapper>
	)
}

export default Component
